import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  IconButton,
  CssBaseline,
} from "@mui/material";
import { PlayArrow, Pause, Replay, LightMode, DarkMode } from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const BOARD_SIZE = 20;
const INITIAL_SNAKE = [{ x: 10, y: 10 }];
const INITIAL_DIRECTION = "RIGHT";
const INITIAL_FOOD = { x: 5, y: 5 };
const GAME_SPEED = 150;

const SnakeGame = () => {
  const [snake, setSnake] = useState(INITIAL_SNAKE);
  const [food, setFood] = useState(INITIAL_FOOD);
  const [direction, setDirection] = useState(INITIAL_DIRECTION);
  const [gameOver, setGameOver] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [mode, setMode] = useState("light");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Theme setup with dark/light mode
  const appTheme = createTheme({
    palette: {
      mode,
      primary: {
        main: mode === "light" ? "#0a84ff" : "#64b5f6",
      },
      secondary: {
        main: mode === "light" ? "#ff9800" : "#ffb74d",
      },
      background: {
        default: mode === "light" ? "#f4f7fa" : "#0d1117",
        paper: mode === "light" ? "#ffffff" : "#161b22",
      },
    },
    shape: { borderRadius: 16 },
    typography: {
      fontFamily: "Inter, sans-serif",
    },
  });

  // Random food generation
  const generateFood = useCallback(() => {
    const newFood = {
      x: Math.floor(Math.random() * BOARD_SIZE),
      y: Math.floor(Math.random() * BOARD_SIZE),
    };
    if (snake.some((s) => s.x === newFood.x && s.y === newFood.y)) {
      return generateFood();
    }
    return newFood;
  }, [snake]);

  // Initialize / Restart
  const initGame = useCallback(() => {
    setSnake(INITIAL_SNAKE);
    setFood(generateFood());
    setDirection(INITIAL_DIRECTION);
    setGameOver(false);
    setScore(0);
    setIsPlaying(true);
  }, [generateFood]);

  // Keyboard input
  const handleKeyPress = useCallback(
    (e) => {
      if (!isPlaying) return;
      const key = e.key.toUpperCase();
      switch (key) {
        case "ARROWUP":
        case "W":
          if (direction !== "DOWN") setDirection("UP");
          break;
        case "ARROWDOWN":
        case "S":
          if (direction !== "UP") setDirection("DOWN");
          break;
        case "ARROWLEFT":
        case "A":
          if (direction !== "RIGHT") setDirection("LEFT");
          break;
        case "ARROWRIGHT":
        case "D":
          if (direction !== "LEFT") setDirection("RIGHT");
          break;
        default:
          break;
      }
    },
    [direction, isPlaying]
  );

  useEffect(() => {
    if (!isPlaying || gameOver) return;

    const moveSnake = () => {
      setSnake((prevSnake) => {
        const head = { ...prevSnake[0] };

        switch (direction) {
          case "UP":
            head.y = (head.y - 1 + BOARD_SIZE) % BOARD_SIZE;
            break;
          case "DOWN":
            head.y = (head.y + 1) % BOARD_SIZE;
            break;
          case "LEFT":
            head.x = (head.x - 1 + BOARD_SIZE) % BOARD_SIZE;
            break;
          case "RIGHT":
            head.x = (head.x + 1) % BOARD_SIZE;
            break;
          default:
            break;
        }

        const collision = prevSnake.some(
          (seg, idx) => idx > 0 && seg.x === head.x && seg.y === head.y
        );
        if (collision) {
          setGameOver(true);
          setIsPlaying(false);
          if (score > highScore) setHighScore(score);
          return prevSnake;
        }

        const newSnake = [head, ...prevSnake];
        if (head.x === food.x && head.y === food.y) {
          setFood(generateFood());
          setScore((s) => s + 1);
        } else newSnake.pop();
        return newSnake;
      });
    };

    const interval = setInterval(moveSnake, GAME_SPEED);
    return () => clearInterval(interval);
  }, [isPlaying, gameOver, direction, food, generateFood, score, highScore]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleKeyPress]);

  // Render board
  const renderBoard = () => {
    const cells = [];
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        const isHead = snake[0].x === x && snake[0].y === y;
        const isSnake = snake.some((s, i) => i !== 0 && s.x === x && s.y === y);
        const isFood = food.x === x && food.y === y;
        let bg = mode === "light" ? "#e0e0e0" : "#2c2f33";

        if (isFood) bg = "#ff1744";
        if (isSnake) bg = mode === "light" ? "#90caf9" : "#42a5f5";
        if (isHead) bg = mode === "light" ? "#0a84ff" : "#64b5f6";

        cells.push(
          <Box
            key={`${x}-${y}`}
            sx={{
              width: isMobile ? 12 : 16,
              height: isMobile ? 12 : 16,
              backgroundColor: bg,
              borderRadius: 2,
              boxShadow: isHead
                ? "0 0 8px rgba(10,132,255,0.6)"
                : isFood
                ? "0 0 8px rgba(255,23,68,0.6)"
                : "none",
              transition: "background-color 0.1s ease",
            }}
          />
        );
      }
    }
    return cells;
  };

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            mode === "light"
              ? "linear-gradient(135deg, #f5f7fa, #c3cfe2)"
              : "radial-gradient(circle at top, #0f2027, #203a43, #2c5364)",
          transition: "all 0.4s ease",
          py: 4,
          px: 2,
        }}
      >
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={8} md={5} lg={4}>
            <Card
              sx={{
                borderRadius: 4,
                backdropFilter: "blur(12px)",
                background: mode === "light"
                  ? "rgba(255, 255, 255, 0.8)"
                  : "rgba(20, 25, 30, 0.6)",
                boxShadow:
                  mode === "light"
                    ? "0 8px 24px rgba(0,0,0,0.1)"
                    : "0 8px 24px rgba(0,0,0,0.6)",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h5" fontWeight="bold">
                    🐍 Snake Game
                  </Typography>
                  <IconButton onClick={() => setMode(mode === "light" ? "dark" : "light")}>
                    {mode === "light" ? <DarkMode /> : <LightMode />}
                  </IconButton>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body1">Score: <strong>{score}</strong></Typography>
                  <Typography variant="body1">High Score: <strong>{highScore}</strong></Typography>
                </Box>

                {/* Controls */}
                <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={() => setIsPlaying(true)}
                    disabled={isPlaying || gameOver}
                  >
                    Start
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Pause />}
                    onClick={() => setIsPlaying(false)}
                    disabled={!isPlaying}
                  >
                    Pause
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<Replay />}
                    onClick={initGame}
                  >
                    Restart
                  </Button>
                </Box>

                {/* Game Board */}
                <Paper
                  elevation={6}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: "2px solid",
                    borderColor: "primary.main",
                    background:
                      mode === "light"
                        ? "linear-gradient(180deg, #f8f9fa, #e9ecef)"
                        : "linear-gradient(180deg, #1c1f26, #14171c)",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${BOARD_SIZE}, 1fr)`,
                      gap: 0.4,
                      width: "100%",
                      aspectRatio: "1 / 1",
                    }}
                  >
                    {renderBoard()}
                  </Box>
                </Paper>

                {gameOver && (
                  <Typography
                    variant="h6"
                    align="center"
                    sx={{
                      mt: 2,
                      color: "error.main",
                      fontWeight: "bold",
                      textShadow: "0 0 8px rgba(255,0,0,0.5)",
                    }}
                  >
                    💀 Game Over! Final Score: {score}
                  </Typography>
                )}

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ mt: 2, color: "text.secondary" }}
                >
                  Use <strong>WASD</strong> or <strong>Arrow Keys</strong> to move.
                </Typography>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ mt: 2, color: "text.primary" }}
                >
                  Powered by <strong>Zolang Nexora</strong>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

export default SnakeGame;
